import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'orcamento.tema';

export type Tema = 'claro' | 'escuro';

type Ctx = {
  tema: Tema;
  definir: (t: Tema) => void;
  alternar: () => void;
};

const TemaContext = createContext<Ctx | null>(null);

/**
 * Lê o tema persistido; se não houver, segue a preferência do sistema
 * operacional (`prefers-color-scheme`). Espelha o script inline do
 * `index.html`, que aplica o mesmo valor antes da primeira pintura para
 * evitar flash de tela clara ao abrir no escuro.
 */
function temaInicial(): Tema {
  try {
    const salvo = localStorage.getItem(STORAGE_KEY);
    if (salvo === 'claro' || salvo === 'escuro') return salvo;
  } catch {
    // localStorage indisponível — cai no fallback abaixo.
  }
  try {
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'escuro';
  } catch {
    // matchMedia indisponível — assume claro.
  }
  return 'claro';
}

function aplicar(tema: Tema): void {
  const raiz = document.documentElement;
  if (tema === 'escuro') raiz.setAttribute('data-theme', 'dark');
  else raiz.removeAttribute('data-theme');
}

/**
 * Tema visual do app (claro/escuro). Aplica `data-theme="dark"` na raiz do
 * documento — todo o CSS reage via variáveis de cor. Persiste em localStorage
 * (preferência por máquina, igual ao `OcultarValoresProvider`).
 */
export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(temaInicial);

  useEffect(() => {
    aplicar(tema);
    try {
      localStorage.setItem(STORAGE_KEY, tema);
    } catch {
      // sem persistência (modo privado/sandbox) — segue só em memória.
    }
  }, [tema]);

  const definir = useCallback((t: Tema) => setTema(t), []);
  const alternar = useCallback(
    () => setTema((v) => (v === 'escuro' ? 'claro' : 'escuro')),
    [],
  );

  const valor = useMemo<Ctx>(() => ({ tema, definir, alternar }), [tema, definir, alternar]);

  return <TemaContext.Provider value={valor}>{children}</TemaContext.Provider>;
}

export function useTema(): Ctx {
  const ctx = useContext(TemaContext);
  if (!ctx) {
    throw new Error('useTema: faltou <TemaProvider> na árvore.');
  }
  return ctx;
}
